import * as React from 'react';
import moment from 'moment';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import GlobalStyles from '@mui/material/GlobalStyles';
import IconButton from '@mui/material/IconButton';
import Portal from '@mui/material/Portal';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { PhotographerCalendarPrintEventsTable } from './PhotographerCalendarPrintEventsTable';
import PhotographerCalendarPrintEventsListRow from './PhotographerCalendarPrintEventsListRow';
import { usePrintEventsQuery } from './queries/usePrintEventsQuery';
import { Translate } from '../../shared/LanguageService';
import { CapitalizeFirstLetter } from '../../components/common/TextHelpers';

type IRange = 'day' | 'week' | 'month';

interface IProps {
    show: boolean;
    hide: () => void;
    photographerId?: number;
}

const hidePageBehindWhenPrinting = {
    '@media print': { '#app, #header, .MuiDialog-root': { display: 'none' } }
};

function rangeAround(selectedDate: Date, range: IRange) {
    let selected = moment(selectedDate);

    switch (range) {
        case 'week':
            return { from: selected.clone().day(1), to: selected.clone().day(1).day(7) };
        case 'month':
            return { from: selected.clone().startOf('month'), to: selected.clone().endOf('month') };
        default:
            return { from: selected.clone(), to: selected.clone() };
    }
}

function datesBetween(startDate: moment.Moment, endDate: moment.Moment) {
    let dates = [startDate.clone().format('YYYYMMDD')];
    if (startDate.format('YYYYMMDD') !== endDate.format('YYYYMMDD')) {
        let selectedDate = moment(startDate.clone().add(1, 'day'));
        while (selectedDate.dayOfYear() !== endDate.dayOfYear()) {
            dates.push(selectedDate.clone().format('YYYYMMDD'));
            selectedDate = selectedDate.add(1, 'day');
        }
        dates.push(moment(endDate).format('YYYYMMDD'));
    }
    return dates;
}

function selectedPeriodLabel(selectedDate: Date, range: IRange) {
    switch (range) {
        case 'week':
            return CapitalizeFirstLetter(
                Translate('photographercalendar/printevents/modal/body/radiobuttons/label/week') +
                    ' ' +
                    moment(selectedDate).format('W')
            );
        case 'month':
            return CapitalizeFirstLetter(moment(selectedDate).format('MMMM'));
        default:
            return CapitalizeFirstLetter(moment(selectedDate).format('dddd[,] DD MMMM'));
    }
}

function printedRange(selectedDate: Date, dates: string[]) {
    let range =
        dates.length === 1
            ? moment(selectedDate).format('YYYY-MM-DD')
            : moment(dates[0]).format('YYYY-MM-DD') +
              ' - ' +
              moment(dates[dates.length - 1]).format('YYYY-MM-DD');
    return `(${range})`;
}

function PhotographerCalendarPrintEvents(props: IProps) {
    let [radioValue, setRadioValue] = React.useState<IRange>('day');
    let [selectedDate, setSelectedDate] = React.useState(new Date());

    let { from, to } = rangeAround(selectedDate, radioValue);
    let selectedDateRange = datesBetween(from, to);
    let { data, isLoading } = usePrintEventsQuery({
        from,
        to,
        photographerId: props.photographerId
    });
    let fetchedItems = data ? data.eventsPerDay : null;

    let changeRange = (event: React.MouseEvent<HTMLElement>, range: IRange | null) => {
        if (range !== null) {
            setRadioValue(range);
        }
    };

    let previousDate = () => setSelectedDate(moment(selectedDate).subtract(1, radioValue).toDate());

    let nextDate = () => setSelectedDate(moment(selectedDate).add(1, radioValue).toDate());

    let print = () => window.print();

    return (
        <>
            <Dialog open={props.show} onClose={props.hide} fullWidth maxWidth="md">
                <GlobalStyles styles={hidePageBehindWhenPrinting} />
                <DialogTitle>
                    {Translate(
                        'photographercalendar/printevents/modal/header/choosedatesforprintouts'
                    )}
                </DialogTitle>
                <DialogContent>
                    <Stack alignItems="center" spacing={1}>
                        <ToggleButtonGroup
                            color="primary"
                            size="small"
                            value={radioValue}
                            exclusive
                            onChange={changeRange}
                        >
                            <ToggleButton value="day">
                                {Translate(
                                    'photographercalendar/printevents/modal/body/radiobuttons/label/day'
                                )}
                            </ToggleButton>
                            <ToggleButton value="week">
                                {Translate(
                                    'photographercalendar/printevents/modal/body/radiobuttons/label/week'
                                )}
                            </ToggleButton>
                            <ToggleButton value="month">
                                {Translate(
                                    'photographercalendar/printevents/modal/body/radiobuttons/label/month'
                                )}
                            </ToggleButton>
                        </ToggleButtonGroup>
                        <Stack direction="row" alignItems="center">
                            <IconButton size="small" onClick={previousDate}>
                                <ChevronLeftIcon />
                            </IconButton>
                            <Box sx={{ minWidth: 200, textAlign: 'center' }}>
                                {selectedPeriodLabel(selectedDate, radioValue)}
                            </Box>
                            <IconButton size="small" onClick={nextDate}>
                                <ChevronRightIcon />
                            </IconButton>
                        </Stack>
                    </Stack>
                    <Box sx={{ marginTop: 2 }}>
                        {fetchedItems && (
                            <PhotographerCalendarPrintEventsTable
                                dates={selectedDateRange}
                                eventsPerDay={fetchedItems}
                            />
                        )}
                    </Box>
                    {isLoading && (
                        <Stack alignItems="center" sx={{ paddingY: 4 }}>
                            <CircularProgress />
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={props.hide}>
                        {Translate('photographercalendar/printevents/modal/footer/close')}
                    </Button>
                    <Button variant="contained" onClick={print}>
                        {Translate('photographercalendar/printevents/modal/footer/print')}
                    </Button>
                </DialogActions>
            </Dialog>
            {props.show && fetchedItems && (
                <Portal>
                    <Box className="center" sx={{ display: 'none', displayPrint: 'block' }}>
                        <div>{selectedPeriodLabel(selectedDate, radioValue)}</div>
                        <div>{printedRange(selectedDate, selectedDateRange)}</div>
                        <div className="row photographer-calendar-table">
                            <table className="table order-table">
                                {selectedDateRange.map((date) =>
                                    (fetchedItems[date] || []).map((item, index) => (
                                        <PhotographerCalendarPrintEventsListRow
                                            key={item.id}
                                            index={index}
                                            item={item}
                                        />
                                    ))
                                )}
                            </table>
                        </div>
                    </Box>
                </Portal>
            )}
        </>
    );
}

export default PhotographerCalendarPrintEvents;
